/**
 * Generates a consistent color class for contact initials based on an ID
 * @param {string|number} id - The ID to generate color from (case_id, contact_id, etc.)
 * @param {string} prefix - Optional prefix for the class name (default: '')
 * @returns {string} - The color class name
 */
export const getContactInitialsColorClass = (id, prefix = '') => {
    if (!id) return '';
    
    // Convert to number for consistent calculation
    const numericId = typeof id === 'string' ? parseInt(id) : id;
    
    // Define available colors - can be extended
    const colors = [
        'blue',
        'green', 
        'red',
        'purple',
        'orange',
        'teal',
        'pink',
        'indigo'
    ];
    
    // Use modulo to get a consistent color index
    const colorIndex = numericId % colors.length;
    const colorClass = colors[colorIndex];
    
    return prefix ? `${prefix}${colorClass}` : colorClass;
};

/**
 * Map of color names to their background color values
 * These match the CSS classes in Cases.css and Contacts.css
 */
const colorMap = {
    'blue': '#c8e6f5',
    'green': '#d4f4dd',
    'red': '#f8d7da',
    'purple': '#e8d5f2',
    'orange': '#ffead1',
    'teal': '#d0f0ec',
    'pink': '#f7d6e6',
    'indigo': '#d6ddf2'
};

/**
 * Gets the inline background color for a given ID
 * @param {string|number} id - The ID to generate color from
 * @returns {string} - The hex color value
 */
export const getContactInitialsColor = (id) => {
    if (!id) return colorMap['blue']; // default color
    
    const numericId = typeof id === 'string' ? parseInt(id) : id;
    const colors = Object.keys(colorMap);
    const colorIndex = numericId % colors.length;
    const colorName = colors[colorIndex];
    
    return colorMap[colorName];
};

/**
 * Generates complete className string for contact initials OR returns inline color
 * @param {string|number} firstParam - Either ID (legacy) or baseClass (new usage)
 * @param {string|number|string[]|object} secondParam - Either baseClass/additionalClasses (legacy), ID (new usage), or options object with {color: true}
 * @param {string|string[]|object} additionalClasses - Additional classes (new usage only) or options object
 * @returns {string|object} - Complete className string OR {backgroundColor: '#color'} if color=true
 */
export const getContactInitialsClassName = (firstParam, secondParam, additionalClasses = []) => {
    let baseClass, id, additional, returnColor = false;
    
    // Check if any parameter is an options object with color: true
    if (typeof secondParam === 'object' && secondParam !== null && !Array.isArray(secondParam) && secondParam.color === true) {
        returnColor = true;
        id = firstParam;
    } else if (typeof additionalClasses === 'object' && additionalClasses !== null && !Array.isArray(additionalClasses) && additionalClasses.color === true) {
        returnColor = true;
        baseClass = firstParam;
        id = secondParam;
    }
    
    // If returnColor is true, return the inline style object
    if (returnColor) {
        return { backgroundColor: getContactInitialsColor(id) };
    }
    
    // Check if first parameter is a string that looks like a class name (contains letters)
    // or if it's clearly a number/numeric string (ID)
    const firstIsClass = typeof firstParam === 'string' && /[a-zA-Z]/.test(firstParam) && !(/^\d+$/.test(firstParam));
    
    if (firstIsClass) {
        // New usage: getContactInitialsClassName('case-initials', id, additionalClasses)
        baseClass = firstParam;
        id = secondParam;
        additional = Array.isArray(additionalClasses) ? additionalClasses : [additionalClasses];
    } else {
        // Legacy usage: getContactInitialsClassName(id) or getContactInitialsClassName(id, baseClass, additionalClasses)
        id = firstParam;
        baseClass = (typeof secondParam === 'string' && /[a-zA-Z]/.test(secondParam)) ? secondParam : 'contact-initials';
        additional = (typeof secondParam === 'string' && /[a-zA-Z]/.test(secondParam)) 
            ? (Array.isArray(additionalClasses) ? additionalClasses : [additionalClasses])
            : (Array.isArray(secondParam) ? secondParam : [secondParam]);
    }
    
    const colorClass = getContactInitialsColorClass(id);
    
    return [baseClass, colorClass, ...additional.filter(Boolean)].join(' ');
};